import { useTranslation } from 'react-i18next';
import React, { useEffect } from 'react';
import { LinearProgress } from '@mui/material';
import { alarmGroups, loadDicts } from '../../../../store/reducers/dicts';
import { useDispatch, useSelector } from 'react-redux';
import * as _foreach from 'lodash/forEach';
import * as _find from 'lodash/find';
import * as _groupBy from 'lodash/groupBy';
import * as _orderBy from 'lodash/orderBy';
import * as _remove from 'lodash/remove';
import { isAdmin, user } from '../../../../store/reducers/user';
import {
    additionalData,
    loading as loadingSelector,
    loadPatientAdditionalData,
    unloadAdditionalData,
    updatePatientAdditionalData,
} from '../../../../store/reducers/additionalData';
import * as moment from 'moment';
import API from '../../../../app/api';
import { addNoteForPatient } from '../../../../store/reducers/notes';
import _ from 'lodash';
import config from '../../../../app/config';
import Grid from '@mui/material/Grid';
import Notes from '../../../../util/notes';
import Formatter from '../../../../util/formatter';
import { setOpenAlarms, setSnackbar } from '../../../../store/reducers/globals';
import AgyleDictionaryField from '../../../../components/ui/selector/agyleDictionaryField';
import AgyleSelectField from '../../../../components/ui/selector/agyleSelectField';
import AgyleTextField from '../../../../components/ui/fields/agyleTextField';
import AgyleDatePicker from '../../../../components/ui/fields/agyleDatePicker';
import AgyleBlockSwitch from '../../../../components/ui/switch/agyleBlockSwitch';
import GroupedAlarms from './groupedAlarms';
import FormLabel from '@mui/material/FormLabel';
import AgyleSwitch from '../../../../components/ui/switch/agyleSwitch';
import InformationBox from '../../../../components/other/informationBox';
import { getAlarmBase } from '../../mainScreen/util/getAlarmBase';
import { AlarmStage } from '../../mainScreen/alarmStage';
import MiniFormDialog from '../../../../components/modal/miniFormDialog';
import { patientBanner } from '../../../../controls/agyle/tasks/components/patientBanner';
import { useAlarmsStyles } from './useAlarmsStyles';

class AlarmMode {
    static #_view = 0;
    static #_edit = 1;
    static #_list = 2;
    static #_ack = 3;

    static get VIEW() {
        return this.#_view;
    }

    static get EDIT() {
        return this.#_edit;
    }

    static get LIST() {
        return this.#_list;
    }
    static get ACKNOWLEDGE() {
        return this.#_ack;
    }
}

class FilterMode {
    static #_active = 0;
    static #_expired = 1;
    static #_all = 2;
    static #_ack = 3;

    static get ACTIVE() {
        return this.#_active;
    }

    static get EXPIRED() {
        return this.#_expired;
    }

    static get ALL() {
        return this.#_all;
    }

    static get ACKNOWLEDGE() {
        return this.#_ack;
    }
}

const PatientAlarms = (props) => {
    const dispatch = useDispatch();
    const alarmGroupDict = useSelector(alarmGroups);
    const userObject = useSelector(user);
    const loadingData = useSelector(loadingSelector);
    const ptAdditionalData = useSelector(additionalData);
    const patientAdditionalData = props?.addData ?? ptAdditionalData;
    const { t } = useTranslation();
    const classes = useAlarmsStyles();
    const tester = Formatter.formatUserFullName(userObject);
    const [modeBody, setModeBody] = React.useState(props.isBB ? AlarmMode.EDIT : AlarmMode.LIST);
    const [alarm, setAlarm] = React.useState(getAlarmBase(props.isBB));
    const [alarmStagesRenderOptions, setAlarmStagesRenderOptions] = React.useState([]);
    const [alarmGroupsRenderOptions, setAlarmGroupsRenderOptions] = React.useState([]);
    const [userIsAdmin, setUserIsAdmin] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [activeAlarms, setActiveAlarms] = React.useState([]);
    const [expiredAlarms, setExpiredAlarms] = React.useState([]);
    const [acknowledgeAlarms, setAcknowledgeAlarms] = React.useState([]);
    const [filterAlarms, setFilterAlarms] = React.useState(FilterMode.ACTIVE);
    const [alarmRenderItems, setAlarmRenderItems] = React.useState([]);

    useEffect(() => {
        if (props.acknowledge && props.open) {
            setModeBody(AlarmMode.ACKNOWLEDGE);
            setFilterAlarms(FilterMode.ACKNOWLEDGE);
        }
    }, [props.acknowledge, props.open]);

    useEffect(() => {
        dispatch(loadDicts);
        let alarmStg = [];
        alarmStg.push({ key: Math.random(), id: AlarmStage.High.color, name: t(AlarmStage.High.name) });
        alarmStg.push({ key: Math.random(), id: AlarmStage.Medium.color, name: t(AlarmStage.Medium.name) });
        alarmStg.push({ key: Math.random(), id: AlarmStage.Low.color, name: t(AlarmStage.Low.name) });
        alarmStg.push({ key: Math.random(), id: AlarmStage.None.color, name: t(AlarmStage.None.name) });
        setAlarmStagesRenderOptions(alarmStg);
    }, []);

    useEffect(() => {
        if (props.open && (props.fromBooking || props.fromSearch) && !_.isNil(props.patient)) {
            if (!props.isBB) dispatch(loadPatientAdditionalData(props.patient.__id));
            else dispatch(unloadAdditionalData());
        }
    }, [props.open, props.fromBooking, props.patient, props.isBB, props.fromSearch]);

    useEffect(() => {
        let groups = [];
        if (alarmGroupDict && Array.isArray(alarmGroupDict)) {
            _foreach(_orderBy(alarmGroupDict, ['order'], ['asc']), (alarmGroupEl) => {
                groups.push({ id: alarmGroupEl.description, name: alarmGroupEl.name });
            });
        }
        setAlarmGroupsRenderOptions(groups);
    }, [alarmGroupDict]);

    useEffect(() => {
        setUserIsAdmin(dispatch(isAdmin()));
    }, [userObject]);

    const getNote = () => {
        return Notes.getPatientAlarmsNote(alarm);
    };

    useEffect(() => {
        if ('save' in alarm) {
            delete alarm.save;
            alarm.userAdd = tester;
            alarm.userAddLogin = userObject?.userName;
            alarm.userAddId = userObject.__id;
            let now = moment();
            alarm.dateAdd = now.valueOf();
            alarm.patientsInWardsRef = props.spellId;
            if (!alarm.expireDate) alarm.expireDate = now.add(1, 'year').valueOf();
            let newArray = [...activeAlarms, ...expiredAlarms];
            if (alarm.id) {
                _remove(newArray, function (aEl) {
                    return aEl.id === alarm.id;
                });
            }

            if (!alarm.deleted) {
                alarm.acknowledgeBy.push({
                    user: userObject?.userName,
                    timestamp: moment().toISOString(),
                });
            }

            // If infection control, update IPC flag if it's not checked
            if (alarm?.group === 'INFECTION CONTROL' && !_.isEmpty(props.currentPiW?.bedRequests)) {
                let clone = _.cloneDeep(props.currentPiW?.bedRequests);
                let bedRequest = _.find(clone, (br) => !br?.followUp && br?.status === 'NEW');
                if (bedRequest?.ipc && !bedRequest?.ipc?.checked) {
                    bedRequest.ipc.checked = true;
                    bedRequest.ipc.user = tester;
                    bedRequest.ipc.login = userObject?.userName;
                    bedRequest.ipc.timestamp = moment().toISOString();
                    bedRequest.ipc.autoSystem = true;

                    // clear object
                    delete bedRequest.__href;
                    delete bedRequest.__type;
                    delete bedRequest.__version;
                    delete bedRequest._createdBy;
                    delete bedRequest._createdDate;

                    API.saveBedRequest(bedRequest)
                        .then((res) => {
                            dispatch(
                                addNoteForPatient(
                                    props.patient.__id,
                                    {
                                        note: Notes.getBedRequestExternalNote(res),
                                        noteType: 'bedRequest',
                                        patientsInWardsRef: [props.currentPiW.__id],
                                        episodeId: props.patient?.admission?.consultantEpisodeId || null,
                                    },
                                    props.currentPiW.__id,
                                ),
                            );
                            if (props.modifyPatient) {
                                props.modifyPatient([
                                    {
                                        name: 'bedRequests',
                                        value: clone,
                                    },
                                ]);
                            }
                        })
                        .catch(() => {
                            dispatch(
                                setSnackbar({
                                    type: 'error',
                                    message: t('There was a problem with saving bed request.'),
                                }),
                            );
                        });
                }
            }

            const wholeArray = [...newArray, alarm];
            if (!props.isBB) {
                dispatch(updatePatientAdditionalData(props.patient.__id, { alarms: wholeArray }));
                dispatch(
                    addNoteForPatient(
                        props.patient.__id,
                        {
                            note: getNote(),
                            noteType: 'alarms',
                            patientsInWardsRef: [props.spellId],
                            episodeId: props.patient?.admission?.consultantEpisodeId || null,
                        },
                        props.patient.__id,
                    ),
                );
                saveAlarmsCount(wholeArray);
            }
            setSaving(true);
            const infectionControl = _.filter(
                wholeArray,
                (a) => a.group === 'INFECTION CONTROL' && !a.deleted && moment(a.expireDate).isSameOrAfter(moment()),
            );
            updatePatient(infectionControl, [...newArray, alarm]);
        }
    }, [alarm]);

    const saveAlarmsCount = (count) => {
        if (count && count.length > 0) {
            const counter = _.filter(count, (el) => {
                return moment(el.expireDate).isAfter(moment());
            });

            API.updatePatientInWard(props.spellId, [
                {
                    'metadata.alertsCount': {
                        count: counter && counter.length > 0 ? counter.length : 0,
                    },
                },
            ])
                .then((response) => {})
                .catch((error) => {
                    console.error('Alerts updatePatientInWard: ', error);
                });
        }
    };

    const updatePatient = (infections, wholeArray) => {
        const dataToUpload = [];
        let infectionsControl = _find(alarmGroupDict, { description: 'INFECTION CONTROL' });
        _.forEach(infectionsControl?.alertNames, (inf) => {
            const obj = _.find(infections, { name: inf });
            const status = obj ? obj.state : false;
            const expired = obj ? obj.expireDate : null;
            let name = _.toLower(inf);

            if (props.isBB) {
                if (inf === 'COVID 19') name = 'covid';
                dataToUpload.push({ [name]: { status, expired } });
            } else {
                if (inf === 'COVID 19') {
                    name = 'covid';
                    dataToUpload.push({ 'metadata.covid': { status, expired } });
                }
                name = 'metadata.infections.' + name;
                dataToUpload.push({ [name]: { status, expired } });
            }
        });
        if (props.isBB) {
            const data = [{ alarms: [wholeArray, dataToUpload] }];
            if (props.fromAdmit) {
                const dataForAdmit = [
                    {
                        name: 'alarms',
                        value: [wholeArray, dataToUpload],
                    },
                ];
                props.onChange(dataForAdmit);
                props.close();
            } else {
                API.updateBedBookingPatients(props.spellId, data)
                    .then((res) => {
                        if (res) dispatch(setSnackbar({ type: 'success', message: 'Patient data saved correctly' }));
                        else dispatch(setSnackbar({ type: 'error', message: 'Patient data not saved correctly' }));
                    })
                    .catch((err) => {
                        dispatch(setSnackbar({ type: 'error', message: 'Patient data not saved correctly' }));
                        console.error(err);
                    })
                    .finally(() => props.close());
            }
        } else {
            API.updatePatientInWard(props.spellId, dataToUpload)
                .catch((error) => console.error('API.updatePatientInWard error: ', error))
                .finally(() => {
                    if (_.isFunction(props?.onRefresh)) {
                        props.onRefresh();
                    }
                });
        }
    };

    useEffect(() => {
        if (!props.isBB && patientAdditionalData.other && patientAdditionalData.other.alarms) {
            const now = moment();
            let activeAlarmsList = [];
            let expiredAlarmsList = [];
            let acknowledgeAlarmsList = [];
            _foreach(patientAdditionalData.other.alarms, (alarmEl) => {
                if (!alarmEl.deleted) {
                    if (moment(alarmEl.expireDate).isBefore(now)) {
                        expiredAlarmsList.push(alarmEl);
                    } else {
                        activeAlarmsList.push(alarmEl);
                        const find = _.find(alarmEl?.acknowledgeBy, { user: userObject?.userName });
                        if (!find) acknowledgeAlarmsList.push(alarmEl);
                    }
                }
            });
            setActiveAlarms(activeAlarmsList);
            setExpiredAlarms(expiredAlarmsList);
            setAcknowledgeAlarms(acknowledgeAlarmsList);
        } else if (props.isBB) {
            let activeAlarmsList = [];
            let expiredAlarmsList = [];
            if (props.patientBB?.alarms && !_.isEmpty(props.patientBB?.alarms?.[0])) {
                const now = moment();
                _foreach(props.patientBB?.alarms[0], (alarmEl) => {
                    if (alarmEl && !alarmEl.deleted) {
                        if (moment(alarmEl.expireDate).isBefore(now)) {
                            expiredAlarmsList.push(alarmEl);
                        } else {
                            activeAlarmsList.push(alarmEl);
                        }
                    }
                });
            }
            setActiveAlarms(activeAlarmsList);
            setExpiredAlarms(expiredAlarmsList);
        } else {
            setActiveAlarms([]);
            setExpiredAlarms([]);
        }
    }, [patientAdditionalData, props.patientBB]);

    useEffect(() => {
        if (saving && loadingData === undefined) {
            setModeBody(AlarmMode.LIST);
            setSaving(false);
        }
    }, [loadingData]);

    useEffect(() => {
        renderListAccordingFilter();
    }, [activeAlarms, expiredAlarms, filterAlarms, alarmGroupsRenderOptions]);

    const getFilteredBaseList = () => {
        // eslint-disable-next-line default-case
        switch (filterAlarms) {
            case FilterMode.ALL:
                return _groupBy([...activeAlarms, ...expiredAlarms], (alarm) => {
                    return alarm.group;
                });
            case FilterMode.ACTIVE:
                return _groupBy([...activeAlarms], (alarm) => {
                    return alarm.group;
                });
            case FilterMode.EXPIRED:
                return _groupBy([...expiredAlarms], (alarm) => {
                    return alarm.group;
                });
            case FilterMode.ACKNOWLEDGE:
                return _groupBy([...acknowledgeAlarms], (alarm) => {
                    return alarm.group;
                });
        }
    };

    const renderListAccordingFilter = () => {
        const baseList = getFilteredBaseList();
        const groupKeys = Object.keys(baseList);
        if (groupKeys && groupKeys.length > 0) {
            let alarmGroups = [];
            _foreach(_orderBy(alarmGroupDict, ['order'], ['asc']), (alarmGroup) => {
                if (groupKeys.indexOf(alarmGroup.description) > -1) {
                    alarmGroups.push(
                        <GroupedAlarms
                            key={'gr_alarms_' + alarmGroup.description}
                            groupName={alarmGroup.description}
                            groupColor={alarmGroup.color}
                            groupAdditionalDesc={alarmGroup.additionalDesc}
                            deleteAlarm={deleteAlarm}
                            alarms={_orderBy(baseList[alarmGroup.description], ['dateAdd'], ['desc'])}
                            acknowledge={modeBody === AlarmMode.ACKNOWLEDGE}
                        />,
                    );
                }
            });
            setAlarmRenderItems(alarmGroups);
        } else {
            setAlarmRenderItems([
                <InformationBox type="info" key="alarms">
                    {t('No alarms currently set')}
                </InformationBox>,
            ]);
        }
    };

    const getNames = () => {
        let names = [];
        if (alarm.group) {
            let foundGroup = _find(alarmGroupDict, { description: alarm.group });
            if (foundGroup && 'alertNames' in foundGroup) {
                names = _.sortBy(foundGroup.alertNames);
            }
        }
        return names;
    };

    const deleteAlarm = (alarmInput) => {
        setAlarm({
            ...alarmInput,
            deleted: true,
            userDelete: tester,
            userDeleteLogin: userObject?.userName,
            userDeleteId: userObject.__id,
            dateDelete: moment().valueOf(),
            save: true,
        });
    };

    const baseDisabled = {
        risk: false,
        banner: false,
        state: false,
    };

    const [disabledFields, setDisabledFields] = React.useState(baseDisabled);
    const findDef = (name) => {
        if (alarm.group) {
            let foundGroup = _find(alarmGroupDict, { description: alarm.group });
            if (foundGroup.def?.[name]) {
                setAlarm({
                    ...alarm,
                    name: name,
                    stage: AlarmStage[foundGroup.def[name].risk].color,
                    onBanner: foundGroup.def[name].banner,
                    state: foundGroup.def[name].state,
                });
            } else {
                setAlarm({
                    ...alarm,
                    name: name,
                });
            }

            if (foundGroup.disabled?.[name]) {
                setDisabledFields(foundGroup.disabled?.[name]);
            } else {
                setDisabledFields(baseDisabled);
            }
        }
    };

    const validateAlarm = () => {
        let status = true;
        let names = getNames();
        if (names.some((n) => n === alarm.name)) {
            _foreach(activeAlarms, (alarmEl) => {
                if (!alarmEl.deleted && alarmEl.name === alarm.name && alarm) {
                    status = false;
                    dispatch(setSnackbar({ type: 'warning', message: 'Alert already added.' }));
                }
            });
        }

        return status;
    };

    const handleClose = () => {
        // eslint-disable-next-line default-case
        switch (modeBody) {
            case AlarmMode.VIEW:
                setModeBody(AlarmMode.LIST);
                break;
            case AlarmMode.LIST:
                props.close();
                break;
            case AlarmMode.EDIT:
                if (props.isBB) props.close();
                else setModeBody(AlarmMode.LIST);
                break;
        }
    };

    const handleSave = () => {
        if (modeBody === AlarmMode.LIST) {
            setAlarm(getAlarmBase(props.isBB));
            setModeBody(AlarmMode.EDIT);
        } else if (modeBody === AlarmMode.EDIT) {
            if (alarm.group && alarm.name && validateAlarm()) {
                setAlarm({ ...alarm, save: true });
            }
        } else if (modeBody === AlarmMode.ACKNOWLEDGE) {
            let orgAlarms = _.cloneDeep(patientAdditionalData?.other?.alarms);

            _.forEach(acknowledgeAlarms, (alarm) => {
                const index = _.findIndex(orgAlarms, { id: alarm.id });
                if (index !== -1) {
                    if (!orgAlarms[index]?.acknowledgeBy) orgAlarms[index].acknowledgeBy = [];
                    orgAlarms[index] = {
                        ...orgAlarms[index],
                        acknowledgeBy: [
                            ...orgAlarms[index]?.acknowledgeBy,
                            {
                                user: userObject?.userName,
                                timestamp: moment().toISOString(),
                            },
                        ],
                    };
                }
            });
            dispatch(setOpenAlarms(false));
            dispatch(updatePatientAdditionalData(props.patient.__id, { alarms: orgAlarms }));
            props.close();
        }
    };

    const submitText = () => {
        // eslint-disable-next-line default-case
        switch (modeBody) {
            case AlarmMode.ACKNOWLEDGE:
                return 'Acknowledge';
            case AlarmMode.LIST:
                return 'Add';
            case AlarmMode.EDIT:
                return 'Submit';
        }
    };

    return (
        !loadingData && (
            <MiniFormDialog
                open={props.open || false}
                onClose={handleClose}
                width="lg"
                fullWidth
                onSave={handleSave}
                submitText={submitText()}
                disableSubmit={
                    (props.disableSave && modeBody !== AlarmMode.ACKNOWLEDGE) ||
                    (modeBody === AlarmMode.EDIT ? saving || alarm.group == null || !alarm.name : false)
                }
                title={t('Alarms')}
                disableActionsCloseButton={modeBody === AlarmMode.ACKNOWLEDGE}
                paperPropsStyle={{ overflowX: 'hidden' }}
            >
                <div>
                    {props.patientInfo && patientBanner(props.patient, true)}

                    {saving && <LinearProgress color="error" />}
                    {(modeBody === AlarmMode.LIST || modeBody === AlarmMode.ACKNOWLEDGE) && (
                        <>
                            {modeBody === AlarmMode.LIST && (
                                <div>
                                    <AgyleBlockSwitch
                                        onChange={setFilterAlarms}
                                        customLabels={{
                                            yes: `[${activeAlarms.length}] ${t('Active alarms')}`,
                                            no: `[${expiredAlarms.length}] ${t('Expired alarms')}`,
                                        }}
                                        valueObject={{
                                            true: FilterMode.ACTIVE,
                                            false: FilterMode.EXPIRED,
                                        }}
                                        value={filterAlarms}
                                    />
                                </div>
                            )}

                            {modeBody === AlarmMode.ACKNOWLEDGE && (
                                <InformationBox type="warning">{t('Please acknowledge new alarms for this patient.')}</InformationBox>
                            )}

                            <div className={classes.alertGroupsList}>{alarmRenderItems}</div>
                        </>
                    )}
                    {modeBody === AlarmMode.EDIT && (
                        <Grid container direction="row" alignItems="center" spacing={1}>
                            <Grid item xs={12}>
                                <FormLabel className={classes.label} required>
                                    {t('Alarm Group')}
                                </FormLabel>

                                <AgyleSelectField
                                    placeholder={t('Please select alarm group')}
                                    disabled={props.isBB}
                                    onChange={(event, val) => {
                                        const gr = _find(alarmGroupDict, (dictEl) => {
                                            return dictEl.description === event;
                                        });
                                        if (gr?.defExpAddDays) {
                                            setAlarm({
                                                ...alarm,
                                                group: event,
                                                name: gr.description === 'CLINICAL' ? 'NEWS Scale 2' : '',
                                                expireDate: moment().add(gr.defExpAddDays, 'days').valueOf(),
                                            });
                                        } else {
                                            setAlarm({
                                                ...alarm,
                                                group: event,
                                                name: event === 'CLINICAL' ? 'NEWS Scale 2' : '',
                                            });
                                        }
                                    }}
                                    options={alarmGroupsRenderOptions}
                                    value={alarm.group}
                                    getOptionLabel={(option) => option.name}
                                    noBorder={false}
                                    withNewBackground
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <FormLabel className={classes.label} required>
                                    {t('Alarm Label')}
                                </FormLabel>

                                <AgyleDictionaryField
                                    placeholder={t('Please select or type alarm label')}
                                    onChange={(event, val) => {
                                        findDef(val);
                                    }}
                                    onBlur={(event) => {
                                        const { value } = event.target;
                                        findDef(value);
                                    }}
                                    clearOnEscape
                                    autoComplete
                                    freeSolo
                                    options={getNames()}
                                    value={alarm.name}
                                    getOptionLabel={(option) => option}
                                    isOptionEqualToValue={(option, value) => option === value}
                                    noBorder={false}
                                    withNewBackground
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <FormLabel className={classes.label}>{t('Description')}</FormLabel>

                                <div>
                                    <AgyleTextField
                                        id="input-description"
                                        multiline
                                        rows={3}
                                        variant="outlined"
                                        onChange={(event) => {
                                            setAlarm({ ...alarm, description: event.target.value });
                                        }}
                                        value={alarm.description}
                                        fullWidth
                                        debounce
                                        noBorder={false}
                                        withNewBackground
                                    />
                                </div>
                            </Grid>

                            <Grid item xs={12}>
                                <FormLabel className={classes.label}>{t('Risk / Priority')}</FormLabel>

                                <AgyleSelectField
                                    disabled={disabledFields.risk}
                                    value={alarm.stage}
                                    withBackground
                                    onChange={(event) => {
                                        setAlarm({ ...alarm, stage: event });
                                    }}
                                    options={alarmStagesRenderOptions}
                                    getOptionLabel={(option) => option.name}
                                    noBorder={false}
                                    withNewBackground
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <FormLabel className={classes.label}>{t('Expiry Date')}</FormLabel>

                                <AgyleDatePicker
                                    ampm={false}
                                    value={alarm.expireDate}
                                    inputFormat={config.defaultDateTimeFormat}
                                    minDateTime={moment()}
                                    onChange={(tDate) => {
                                        if (tDate) setAlarm({ ...alarm, expireDate: tDate.valueOf() });
                                    }}
                                    disableFuture={false}
                                    disablePast={false}
                                    noBorder={false}
                                    withNewBackground
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <AgyleSwitch
                                    label={t('Banner')}
                                    disabled={disabledFields.banner}
                                    onChange={(event) => {
                                        setAlarm({ ...alarm, onBanner: event.target.checked });
                                    }}
                                    value={alarm.onBanner}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <AgyleBlockSwitch
                                    onChange={(event) => setAlarm({ ...alarm, state: event })}
                                    value={alarm.state}
                                    disabled={disabledFields.state}
                                    exclusive
                                    fullWidth
                                    nullState
                                    customLabels={{
                                        yes: t('Suspected'),
                                        empty: t(alarm.group === 'INFECTION CONTROL' ? 'Positive' : 'Confirmed'),
                                        no: t(alarm.group === 'INFECTION CONTROL' ? 'Negative' : 'None'),
                                    }}
                                    valueObject={{
                                        true: 'suspected',
                                        null: 'confirm',
                                        false: 'none',
                                    }}
                                />
                            </Grid>
                        </Grid>
                    )}
                </div>
            </MiniFormDialog>
        )
    );
};

export default PatientAlarms;
